import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosInstance';
import base_url from '../../../constants';
import useNotification from '../../common/Notification';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expiredFilter, setExpiredFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [editFormData, setEditFormData] = useState({
    subscription_end: '',
    extend_days: '',
    updateType: 'date', // 'date' or 'extend'
  });
  const [bulkUpdateFormData, setBulkUpdateFormData] = useState({
    subscription_end: '',
    extend_days: '',
    updateType: 'date',
  });
  const notify = useNotification();

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
      });
      if (search) params.append('search', search);
      if (expiredFilter !== '') params.append('expired', expiredFilter);

      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/subscriptions?${params}`
      );

      if (response.data && response.data.success) {
        setSubscriptions(response.data.data.data || []);
        setMeta({
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          total: response.data.data.total,
          last_page: response.data.data.last_page,
        });
      } else {
        throw new Error('Failed to fetch subscriptions');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch subscriptions';
      setError(errorMessage);
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, expiredFilter]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleEditClick = (subscription) => {
    setSelectedSubscription(subscription);
    const endDate = subscription.subscription_end
      ? new Date(subscription.subscription_end).toISOString().slice(0, 16)
      : '';
    setEditFormData({
      subscription_end: endDate,
      extend_days: '',
      updateType: 'date',
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedSubscription) return;

    try {
      const payload = {};
      if (editFormData.updateType === 'date') {
        if (!editFormData.subscription_end) {
          notify('Please provide subscription end date', 'error');
          return;
        }
        payload.subscription_end = editFormData.subscription_end;
      } else {
        if (!editFormData.extend_days || editFormData.extend_days < 1) {
          notify('Please provide valid number of days to extend', 'error');
          return;
        }
        payload.extend_days = parseInt(editFormData.extend_days);
      }

      const response = await axiosInstance.put(
        `${base_url}/whatsapp/business/admin/subscriptions/${selectedSubscription.user_id}`,
        payload
      );

      if (response.data && response.data.success) {
        notify('Subscription updated successfully', 'success');
        setEditModalOpen(false);
        fetchSubscriptions();
      } else {
        throw new Error('Failed to update subscription');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update subscription';
      notify(errorMessage, 'error');
      console.error('Error updating subscription:', err);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUserIds.length === 0) {
      notify('Please select at least one user', 'error');
      return;
    }

    try {
      const payload = {
        user_ids: selectedUserIds,
      };

      if (bulkUpdateFormData.updateType === 'date') {
        if (!bulkUpdateFormData.subscription_end) {
          notify('Please provide subscription end date', 'error');
          return;
        }
        payload.subscription_end = bulkUpdateFormData.subscription_end;
      } else {
        if (!bulkUpdateFormData.extend_days || bulkUpdateFormData.extend_days < 1) {
          notify('Please provide valid number of days to extend', 'error');
          return;
        }
        payload.extend_days = parseInt(bulkUpdateFormData.extend_days);
      }

      const response = await axiosInstance.post(
        `${base_url}/whatsapp/business/admin/subscriptions/bulk-update`,
        payload
      );

      if (response.data && response.data.success) {
        notify(`Updated ${response.data.updated_count || 0} subscription(s)`, 'success');
        if (response.data.failed && response.data.failed.length > 0) {
          console.warn('Some updates failed:', response.data.failed);
        }
        setBulkUpdateModalOpen(false);
        setSelectedUserIds([]);
        fetchSubscriptions();
      } else {
        throw new Error('Failed to update subscriptions');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update subscriptions';
      notify(errorMessage, 'error');
      console.error('Error bulk updating subscriptions:', err);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUserIds(subscriptions.map((sub) => sub.user_id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectOne = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by user name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <TextField
              select
              label="Expired"
              size="small"
              value={expiredFilter}
              onChange={(e) => {
                setExpiredFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Expired</MenuItem>
              <MenuItem value="false">Active</MenuItem>
            </TextField>
            <IconButton onClick={fetchSubscriptions} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
          {selectedUserIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setBulkUpdateFormData({
                  subscription_end: '',
                  extend_days: '',
                  updateType: 'date',
                });
                setBulkUpdateModalOpen(true);
              }}
            >
              Bulk Update ({selectedUserIds.length})
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell padding="checkbox" sx={{ color: 'white' }}>
                <Checkbox
                  indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < subscriptions.length}
                  checked={subscriptions.length > 0 && selectedUserIds.length === subscriptions.length}
                  onChange={handleSelectAll}
                  sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                />
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subscription End</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Days Remaining</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Expired</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">No subscriptions found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((subscription) => (
                <TableRow key={subscription.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUserIds.includes(subscription.user_id)}
                      onChange={() => handleSelectOne(subscription.user_id)}
                    />
                  </TableCell>
                  <TableCell>{subscription.user_id}</TableCell>
                  <TableCell>{subscription.user?.name || '-'}</TableCell>
                  <TableCell>{subscription.user?.email || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.status}
                      size="small"
                      color={subscription.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(subscription.subscription_end)}</TableCell>
                  <TableCell>
                    <Typography color={subscription.days_remaining >= 0 ? 'text.primary' : 'error'}>
                      {subscription.days_remaining !== undefined ? subscription.days_remaining : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.is_expired ? 'Yes' : 'No'}
                      size="small"
                      color={subscription.is_expired ? 'error' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditClick(subscription)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={meta.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 15, 25, 50]}
      />

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Update Type"
              value={editFormData.updateType}
              onChange={(e) => setEditFormData({ ...editFormData, updateType: e.target.value })}
              fullWidth
            >
              <MenuItem value="date">Set Specific Date</MenuItem>
              <MenuItem value="extend">Extend by Days</MenuItem>
            </TextField>
            {editFormData.updateType === 'date' ? (
              <TextField
                label="Subscription End Date"
                type="datetime-local"
                value={editFormData.subscription_end}
                onChange={(e) => setEditFormData({ ...editFormData, subscription_end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            ) : (
              <TextField
                label="Extend by Days"
                type="number"
                value={editFormData.extend_days}
                onChange={(e) => setEditFormData({ ...editFormData, extend_days: e.target.value })}
                inputProps={{ min: 1 }}
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Update Modal */}
      <Dialog open={bulkUpdateModalOpen} onClose={() => setBulkUpdateModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Update Subscriptions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Updating {selectedUserIds.length} subscription(s)
            </Typography>
            <TextField
              select
              label="Update Type"
              value={bulkUpdateFormData.updateType}
              onChange={(e) => setBulkUpdateFormData({ ...bulkUpdateFormData, updateType: e.target.value })}
              fullWidth
            >
              <MenuItem value="date">Set Specific Date</MenuItem>
              <MenuItem value="extend">Extend by Days</MenuItem>
            </TextField>
            {bulkUpdateFormData.updateType === 'date' ? (
              <TextField
                label="Subscription End Date"
                type="datetime-local"
                value={bulkUpdateFormData.subscription_end}
                onChange={(e) => setBulkUpdateFormData({ ...bulkUpdateFormData, subscription_end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            ) : (
              <TextField
                label="Extend by Days"
                type="number"
                value={bulkUpdateFormData.extend_days}
                onChange={(e) => setBulkUpdateFormData({ ...bulkUpdateFormData, extend_days: e.target.value })}
                inputProps={{ min: 1 }}
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateModalOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkUpdate} variant="contained">Update All</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscriptions;

